import { useMemo } from "react"

import type { TrackingEventName } from "@/lib/analytics"
import { useStringArrayActions } from "@/hooks/filter-params/useStringArrayActions"

import type { FilterState } from "../types/filterState"

interface UseTrackedRunsStringArrayActionsParams {
  values: string[]
  update: (updater: (previous: string[]) => string[]) => void
  trackEvent: (
    event: TrackingEventName,
    properties?: Record<string, unknown>,
  ) => void
  addedEvent: TrackingEventName
  removedEvent: TrackingEventName
  bulkUpdatedEvent: TrackingEventName
  valuePropertyName: string
  countPropertyName: string
  totalActiveFilters: number
}

export function countActiveRunsFilters(state: FilterState): number {
  const listFilterCount = [
    state.statusFilters,
    state.templateFilters,
    state.personaFilters,
    state.agentFilters,
    state.metricFilters,
    state.metricValueFilters,
    state.tagFilters,
    state.mutationFilters,
    state.testSetFilters,
    state.testCaseFilters,
    state.metadataFilters ?? [],
    state.scheduledJobFilters,
  ].reduce((count, filters) => count + filters.length, 0)
  const dateFilterCount = state.dateRange.start || state.dateRange.end ? 1 : 0
  const searchFilterCount = state.searchValue.trim() ? 1 : 0

  return listFilterCount + dateFilterCount + searchFilterCount
}

export function useTrackedRunsStringArrayActions({
  values,
  update,
  trackEvent,
  addedEvent,
  removedEvent,
  bulkUpdatedEvent,
  valuePropertyName,
  countPropertyName,
  totalActiveFilters,
}: UseTrackedRunsStringArrayActionsParams) {
  const actions = useStringArrayActions(update)

  return useMemo(
    () => ({
      add: (value: string) => {
        actions.add(value)
        trackEvent(addedEvent, {
          [valuePropertyName]: value,
          [countPropertyName]: values.length + 1,
          total_active_filters: totalActiveFilters,
        })
      },
      remove: (value: string) => {
        actions.remove(value)
        trackEvent(removedEvent, {
          [valuePropertyName]: value,
          [countPropertyName]: values.length - 1,
          total_active_filters: totalActiveFilters,
        })
      },
      set: (filters: string[]) => {
        actions.set(filters)
        trackEvent(bulkUpdatedEvent, {
          filter_count: filters.length,
          total_active_filters: totalActiveFilters,
        })
      },
    }),
    [
      actions,
      addedEvent,
      bulkUpdatedEvent,
      countPropertyName,
      removedEvent,
      totalActiveFilters,
      trackEvent,
      valuePropertyName,
      values.length,
    ],
  )
}
